import React from "react";
import { QueryClient, QueryClientProvider, QueryKey } from "react-query";
import { BackendResponse } from "./BackendResponse";
import { incrementValueVersion } from "../utils/incrementValueVersion";

export interface CvpQueryClient {
  /** stall updates of already fetched queries to avoid unnecessary UI updates */
  stallUpdates: boolean;
  queryClient: QueryClient;
  invalidateQueries: QueryClient["invalidateQueries"];
}
export const CvpQueryClientContext = React.createContext<CvpQueryClient>(null);
export function useCvpQueryClient() {
  return React.useContext(CvpQueryClientContext);
}

export const queryClient = getQueryClient();

export const CvpQueryClientProvider = ({ children }) => {
  const [stallUpdates, setStallUpdates] = React.useState(false);

  return (
    <CvpQueryClientContext.Provider
      value={{
        stallUpdates: stallUpdates,
        queryClient,
        invalidateQueries: async (...args) => {
          setStallUpdates(true);
          const result = await queryClient.invalidateQueries(...args);
          setStallUpdates(false);
          return result;
        },
      }}
    >
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </CvpQueryClientContext.Provider>
  );
};

function getQueryClient() {
  const queryClient = new QueryClient();
  try {
    hydrateQueryClient(queryClient);
  } catch (e) {
    console.error("hydrateQueryClient failed", e);
  }
  return queryClient;
}

// hydrate the QueryClient with the data from the server
// then we dont have to wait for api calls like whoAmI before render
function hydrateQueryClient(queryClient: QueryClient) {
  const getReactQueryData = (window as any).getReactQueryData;
  if (!getReactQueryData) return;

  const queryData = getReactQueryData();
  for (const key in queryData) {
    const data = queryData[key];

    // Skip null/undefined seeds. The server seeds some queries as null when it
    // can't resolve the payload for the current request (e.g. HtmlController's
    // GetCandidateInfo returns null when the logged-in user has no verified
    // email link to the candidate — which is the normal case during operator
    // impersonation). Seeding null here would spread into an empty
    // { ok:true, statusCode:200 } response with no payload, and because
    // DEFAULT_QUERY_OPTIONS uses staleTime: Infinity react-query would treat
    // that empty entry as fresh forever and never call the real endpoint —
    // leaving e.g. the profile form blank even though /cvp/CvpGetCandidate
    // returns full data. Skipping the seed lets the query fetch normally; the
    // fetched endpoint is still token-gated server-side, so this does not widen
    // access to anyone else's data.
    if (data == null) continue;

    const response: BackendResponse = {
      valueVersion: incrementValueVersion(),
      statusCode: 200,
      ok: true,
      ...data,
    };

    queryClient.setQueryData([key], response);
  }
}
